import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Send, X, MessageCircle, CheckCheck, ChevronDown } from "lucide-react";

const ChatModal = ({ isOpen, onClose, relatedType, relatedId, title, apiPrefix = "/chat" }) => {
  const { api, user } = useAuth();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setConversationId(null); setMessages([]); setMessage(""); setError("");
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !relatedType || !relatedId) return;
    let active = true;
    const initConversation = async () => {
      try {
        setLoading(true);
        const response = await api.post(`${apiPrefix}/conversations`, { related_type: relatedType, related_id: relatedId });
        if (!active) return;
        const convoId = response.data.id;
        setConversationId(convoId);
        const messagesRes = await api.get(`${apiPrefix}/conversations/${convoId}/messages`);
        if (!active) return;
        setMessages(messagesRes.data || []);
        await api.post(`${apiPrefix}/conversations/${convoId}/mark-read`).catch(() => {});
      } catch { setError("Impossible de charger la conversation."); }
      finally { if (active) setLoading(false); }
    };
    initConversation();
    return () => { active = false; };
  }, [api, isOpen, relatedType, relatedId]);

  useEffect(() => {
    if (!conversationId || !isOpen) return;
    pollRef.current = setInterval(async () => {
      try { const res = await api.get(`${apiPrefix}/conversations/${conversationId}/messages`); setMessages(res.data || []); } catch {}
    }, 3000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [conversationId, isOpen, api]);

  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async () => {
    if (!message.trim() || !conversationId) return;
    const content = message.trim();
    setMessage(""); setSending(true);
    try {
      await api.post(`${apiPrefix}/conversations/${conversationId}/messages`, { content });
      const res = await api.get(`${apiPrefix}/conversations/${conversationId}/messages`);
      setMessages(res.data || []);
    } catch { setError("Erreur envoi message."); }
    finally { setSending(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="chat-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Chat container - bottom sheet on mobile, centered on desktop */}
      <div className="absolute inset-0 flex items-end sm:items-center sm:justify-center pointer-events-none">
        <div
          className="pointer-events-auto w-full sm:w-[460px] sm:max-w-[90vw] bg-[#0e0e16] border border-white/10 sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: 'min(600px, 85vh)', height: 'min(600px, 85vh)' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-modal-container"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0 bg-[#111118]">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: 'var(--theme-primary, #7c3aed)' }}>
                {(title || "C")[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-white truncate">{title || "Chat"}</h3>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <p className="text-[10px] text-gray-500">En ligne</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-all shrink-0"
              data-testid="chat-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages area */}
          <div
            className="flex-1 overflow-y-auto px-4 py-3 min-h-0"
            style={{ WebkitOverflowScrolling: 'touch' }}
            data-testid="chat-message-list"
          >
            {loading && (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {error && <div className="text-sm text-red-400 text-center py-3 bg-red-500/10 rounded-xl">{error}</div>}
            {!loading && messages.length === 0 && !error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageCircle className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Aucun message</p>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              {messages.map((msg) => {
                const isMe = msg.sender_id === user?.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[78%]">
                      {!isMe && <span className="text-[10px] text-gray-500 ml-1 mb-0.5 block">{msg.sender_name || msg.sender_role}</span>}
                      <div className={`px-3 py-2 text-[13px] leading-relaxed break-words ${
                        isMe
                          ? "rounded-2xl rounded-br-sm text-white"
                          : "bg-white/[0.07] rounded-2xl rounded-bl-sm text-gray-200 border border-white/[0.06]"
                      }`} style={isMe ? { background: 'var(--theme-primary, #7c3aed)' } : {}}>
                        {msg.content}
                        <div className={`flex items-center gap-1 mt-0.5 ${isMe ? 'justify-end' : ''}`}>
                          <span className={`text-[9px] ${isMe ? 'text-white/50' : 'text-gray-600'}`}>
                            {msg.created_at ? new Date(msg.created_at).toLocaleString("fr-FR", { hour: '2-digit', minute: '2-digit' }) : ""}
                          </span>
                          {isMe && <CheckCheck className="w-3 h-3 text-white/40" />}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div ref={endRef} />
          </div>

          {/* Input area */}
          <div className="px-3 py-2.5 border-t border-white/10 shrink-0 bg-[#111118]">
            <div className="flex items-end gap-2">
              <input
                ref={inputRef}
                type="text"
                className="flex-1 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-purple-500/50 outline-none transition-all"
                placeholder="Ecrire un message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                data-testid="chat-message-input"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !message.trim()}
                className="w-10 h-10 rounded-xl text-white flex items-center justify-center transition-all disabled:opacity-30 shrink-0"
                style={{ background: 'var(--theme-primary, #7c3aed)' }}
                data-testid="chat-send-button"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
